/** Formatação pt-BR (07 §10): 1.234,56 · 29/09/2026 · 14:05 · R$ 1.234,56. Instâncias de Intl criadas uma vez por (tipo, decimais). */

const cacheNumero = new Map<string, Intl.NumberFormat>();

function numero(decimais: number, moeda = false): Intl.NumberFormat {
  const chave = `${moeda ? 'm' : 'n'}${decimais}`;
  let f = cacheNumero.get(chave);
  if (!f) {
    f = new Intl.NumberFormat('pt-BR', moeda
      ? { style: 'currency', currency: 'BRL', minimumFractionDigits: decimais, maximumFractionDigits: decimais }
      : { minimumFractionDigits: decimais, maximumFractionDigits: decimais });
    cacheNumero.set(chave, f);
  }
  return f;
}

export function formatarNumero(v: number | string | null | undefined, decimais = 0): string {
  if (v === null || v === undefined || v === '') return '';
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? numero(decimais).format(n) : String(v);
}

export function formatarMoeda(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '';
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? numero(2, true).format(n).replace(/ /g, ' ') : String(v);
}

/** Aceita 'AAAA-MM-DD', ISO com hora ou Date; devolve 'DD/MM/AAAA' (e ' HH:MM' se `comHora`). */
export function formatarData(v: string | Date | null | undefined, comHora = false): string {
  if (!v) return '';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const [a, m, d] = v.split('-');
    return `${d}/${m}/${a}`;
  }
  const data = typeof v === 'string' ? new Date(v) : v;
  if (Number.isNaN(data.getTime())) return String(v);
  const p = (n: number) => String(n).padStart(2, '0');
  const texto = `${p(data.getDate())}/${p(data.getMonth() + 1)}/${data.getFullYear()}`;
  return comHora ? `${texto} ${p(data.getHours())}:${p(data.getMinutes())}` : texto;
}

/** CPF (11) ou CNPJ (14, inclusive alfanumérico nas 12 primeiras posições). */
export function formatarCpfCnpj(v: string | null | undefined): string {
  if (!v) return '';
  const s = v.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
  if (s.length === 11) return `${s.slice(0, 3)}.${s.slice(3, 6)}.${s.slice(6, 9)}-${s.slice(9)}`;
  if (s.length === 14) return `${s.slice(0, 2)}.${s.slice(2, 5)}.${s.slice(5, 8)}/${s.slice(8, 12)}-${s.slice(12)}`;
  return v;
}

/** Telefone brasileiro: 10 dígitos → (47) 3322-4400, 11 → (47) 99123-4567. Outro tamanho (ramal, exterior) passa como veio. */
export function formatarTelefone(v: string | null | undefined): string {
  if (!v) return '';
  const d = v.replace(/\D/g, '');
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  return v;
}

export function formatarCep(v: string | null | undefined): string {
  if (!v) return '';
  const d = v.replace(/\D/g, '');
  return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : v;
}

/**
 * Máscara do dicionário: '9' dígito, 'A' letra, 'X' alfanumérico, demais caracteres literais.
 * O prefixo '@R ' (formato herdado do dicionário) é ignorado pelo adaptador antes de chegar aqui.
 */
export function aplicarMascara(valor: string, mascara: string): string {
  const limpo = valor.replace(/[^0-9A-Za-z]/g, '');
  let saida = '';
  let i = 0;
  for (const m of mascara) {
    if (i >= limpo.length) break;
    const c = limpo[i];
    if (m === '9') {
      if (!/\d/.test(c)) { i++; continue; }
      saida += c; i++;
    } else if (m === 'A') {
      if (!/[A-Za-z]/.test(c)) { i++; continue; }
      saida += c.toUpperCase(); i++;
    } else if (m === 'X') {
      saida += c.toUpperCase(); i++;
    } else {
      saida += m;
    }
  }
  return saida;
}

/** Só dígitos (até `maximo`). */
export const soDigitos = (v: string | null | undefined, maximo = 99) => (v ?? '').replace(/\D/g, '').slice(0, maximo);

/** CPF ou CNPJ enquanto digita: até 11 dígitos no formato do CPF, de 12 a 14 no do CNPJ. */
export function mascararCpfCnpj(v: string | null | undefined): string {
  const d = soDigitos(v, 14);
  return d.length <= 11 ? aplicarMascara(d, '999.999.999-99') : aplicarMascara(d, '99.999.999/9999-99');
}

export const mascararCep = (v: string | null | undefined) => aplicarMascara(soDigitos(v, 8), '99999-999');

/** Barras da data enquanto digita (29092026 → 29/09/2026). Atalhos (h, b, +7) e o que não é só dígito e barra passam intactos. */
export function mascararData(texto: string): string {
  if (!/^[\d/]+$/.test(texto)) return texto;
  const d = texto.replace(/\D/g, '').slice(0, 8);
  // Até 4 dígitos pode ser a digitação rápida (2909 = 29/09 do ano corrente): a barra entra só entre dia e mês.
  return d.length <= 2 ? (texto.endsWith('/') && d.length === 2 ? `${d}/` : d) : d.length <= 4 ? `${d.slice(0, 2)}/${d.slice(2)}` : `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/** Converte digitação pt-BR ('1.234,5') para número; vazio → null. */
export function lerNumero(texto: string): number | null {
  const t = texto.trim();
  if (!t) return null;
  const n = Number(t.replace(/\./g, '').replace(',', '.').replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

/**
 * Digitação rápida de data (07 §3.3): '290926' → 29/09/2026, 'h' hoje, 'b' data-base, '+7' hoje+7.
 * Devolve 'AAAA-MM-DD' ou null se não reconhecer.
 */
export function lerData(texto: string, hoje: string, dataBase: string): string | null {
  const t = texto.trim().toLowerCase();
  if (!t) return null;
  if (t === 'h') return hoje;
  if (t === 'b') return dataBase;
  const soma = /^([+-])(\d{1,4})$/.exec(t);
  if (soma) {
    const d = new Date(`${hoje}T12:00:00`);
    d.setDate(d.getDate() + (soma[1] === '-' ? -1 : 1) * Number(soma[2]));
    return d.toISOString().slice(0, 10);
  }
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (iso) return valida(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  const digitos = t.replace(/\D/g, '');
  const ano = Number(hoje.slice(0, 4));
  if (digitos.length === 6) return valida(2000 + Number(digitos.slice(4, 6)), Number(digitos.slice(2, 4)), Number(digitos.slice(0, 2)));
  if (digitos.length === 8) return valida(Number(digitos.slice(4, 8)), Number(digitos.slice(2, 4)), Number(digitos.slice(0, 2)));
  if (digitos.length === 4) return valida(ano, Number(digitos.slice(2, 4)), Number(digitos.slice(0, 2)));
  return null;
}

function valida(a: number, m: number, d: number): string | null {
  const data = new Date(Date.UTC(a, m - 1, d));
  if (data.getUTCFullYear() !== a || data.getUTCMonth() !== m - 1 || data.getUTCDate() !== d) return null;
  return data.toISOString().slice(0, 10);
}
